import React, { FC, ReactNode, Suspense, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from 'utils/auth';
import * as Sentry from '@sentry/react';
import PageError from 'components/common/PageError';
import PageLoading from 'components/common/PageLoading';
import MaintenancePage from 'components/common/MaintenancePage';
import { MAINTENANCE_MODE } from 'config/flags';
import { useLogin } from 'hooks/useLogin';
import useRegion from 'hooks/useRegion';

const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Loading CSS chunk')
  );
};

const CHUNK_RELOAD_KEY = 'chunk_error_reloaded';

const ChunkErrorFallback: FC<{ error: unknown; resetError: () => void }> = ({
  error,
  resetError,
}) => {
  const chunkError = isChunkLoadError(error);
  const hasAlreadyReloaded =
    sessionStorage.getItem(CHUNK_RELOAD_KEY) === 'true';

  useEffect(() => {
    if (chunkError && !hasAlreadyReloaded) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
      window.location.reload();
    }
  }, [chunkError, hasAlreadyReloaded]);

  if (chunkError && !hasAlreadyReloaded) {
    return <PageLoading />;
  }

  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  return <PageError resetError={resetError} />;
};

export const AnonymousRoute: FC<{ children: ReactNode }> = ({ children }) => (
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <ChunkErrorFallback error={error} resetError={resetError} />
    )}>
    <Suspense fallback={<PageLoading />}>{children}</Suspense>
  </Sentry.ErrorBoundary>
);

export const withRoute = (component: React.FC) => {
  const { isAuthenticated, isLoading } = useAuth();
  const Component = MAINTENANCE_MODE ? MaintenancePage : component;

  return (
    <>
      {isAuthenticated && !isLoading ? (
        <AnonymousRoute>
          <Component />
        </AnonymousRoute>
      ) : (
        <PageLoading />
      )}
    </>
  );
};

export const withProtectedRoute = (component: React.FC) => {
  useLogin();

  return withRoute(component);
};

export const withRegionLockedRoute = (
  component: React.FC,
  fallback: string
) => {
  const RegionGuard = () => {
    const { loading, isEUUser } = useRegion();
    const Component = component;

    if (loading) return <PageLoading />;

    if (!isEUUser) return <Navigate to={fallback} replace />;

    return <Component />;
  };

  return withRoute(RegionGuard);
};
